import React, { useEffect, useState } from "react";
import { IoMdClose } from "react-icons/io";
import { TbDoorEnter, TbDoorExit, TbUsers } from "react-icons/tb";
import { Select, Button } from "@mantine/core";
import { sensorType } from "../interfaces/groups";
import { DoorDisplay } from "../interfaces/doors";
import { notifications } from "@mantine/notifications";
import SensorLogs from "./SensorLogs";
import { getDoors } from "../redux/slices/doors.slice";
import {
  useUpdateDoorInfoMutation,
  useAssignDoorSensorMutation,
  useUnassignDoorSensorMutation,
} from "../redux/slices/api.slice";
import { useAppDispatch } from "../redux/hooks";
import { SearchableSelect } from "./SearchableSelect";

type propType = {
  setOpenControlBar: (input) => void;
  availableSensors: sensorType[];
  // the floor's records joined with their sensor-derived display status
  doors: DoorDisplay[];
  doorInfo?: DoorDisplay;
  setDoorInfo: any;
};

const DoorControlBar = ({
  setOpenControlBar,
  availableSensors,
  doors,
  doorInfo,
  setDoorInfo,
}: propType) => {
  const dispatch = useAppDispatch();

  const [selectedSensor, setSelectedSensor] = useState("");
  const [doorName, setDoorName] = useState("");
  const [doorStatus, setDoorStatus] = useState("");
  const [openSensorLogs, setOpenSensorLogs] = useState(false);
  const s = availableSensors.map((s) => s.id);

  // an svg shape with no BE record yet — only sensor assignment applies
  const isUnassigned = !doorInfo.id;
  // the alias is the join key to the svg shape (door-N), renaming would
  // orphan the shape, so the name is locked for shape-bound doors
  const nameLocked = /^door-\d+$/i.test(doorInfo.alias || "");

  const Status = [
    { label: "Active", value: "active" },
    { label: "Blocked", value: "Blocked" },
    { label: "Under Maintenance", value: "Under Maintenance" },
  ];
  const sensors = Array.from(
    new Set(["No sensor", ...s, doorInfo.sensorID].filter(Boolean))
  );

  const [updateDoorInfo, { isLoading }] = useUpdateDoorInfoMutation();
  const [assignDoorSensor, { isLoading: isAssigning }] =
    useAssignDoorSensorMutation();
  const [unassignDoorSensor, { isLoading: isUnassigning }] =
    useUnassignDoorSensorMutation();

  useEffect(() => {
    // an unassigned shape has no record id yet — once assignment creates the
    // record, pick it up by its alias (the shape id) and switch to it
    const door = doors?.find((d) =>
      doorInfo.id ? d.id === doorInfo.id : d.alias === doorInfo.alias
    );

    if (door) {
      setDoorInfo(door);
    }
  }, [doors]);

  useEffect(() => {
    setSelectedSensor(doorInfo.sensorID);
    setDoorName(doorInfo.alias);
    setDoorStatus(doorInfo.status);
  }, [doorInfo]);

  const handleEditName = async (e) => {
    e.preventDefault();
    try {
      const res = await updateDoorInfo({
        doorID: doorInfo.id,
        updatedData: {
          alias: doorName,
          floorID: doorInfo.floorID,
        },
        action: "update",
      });
      //@ts-ignore
      if (res.data) {
        notifications.show({
          title: "Success",
          message: "Name edited successfully",
          color: "green",
        });
        dispatch(getDoors({ floorID: doorInfo.floorID }));
      } else {
        notifications.show({
          title: "Error",
          message: "something went wrong , try again!",
          color: "red",
        });
      }
    } catch (error) {
      console.error("Mutation error:", error);
      notifications.show({
        title: "Error",
        message: "something went wrong , try again!",
        color: "red",
      });
    }
  };

  // assignment is keyed on the sensor, so a swap unassigns the old one first
  const handleAssignSensor = async (e) => {
    e.preventDefault();

    const currentSensor =
      doorInfo.sensorID && doorInfo.sensorID !== "No sensor"
        ? doorInfo.sensorID
        : "";
    const nextSensor =
      selectedSensor && selectedSensor !== "No sensor" ? selectedSensor : "";
    if (currentSensor === nextSensor) return;

    try {
      let ok = true;
      if (currentSensor) {
        const res = await unassignDoorSensor({ sensorID: currentSensor });
        //@ts-ignore
        ok = !!res.data;
      }
      if (ok && nextSensor) {
        const res = await assignDoorSensor({
          sensorID: nextSensor,
          floorID: doorInfo.floorID,
          alias: doorInfo.alias,
        });
        //@ts-ignore
        ok = !!res.data;
      }
      if (ok) {
        dispatch(getDoors({ floorID: doorInfo.floorID }));
        notifications.show({
          title: "Success",
          message: "Sensor edited successfully",
          color: "green",
        });
      } else {
        notifications.show({
          title: "Error",
          message: "something went wrong , try again!",
          color: "red",
        });
      }
    } catch (error) {
      console.error("Mutation error:", error);
      notifications.show({
        title: "Error",
        message: "something went wrong , try again!",
        color: "red",
      });
    }
  };

  const handleEditStatus = async (e) => {
    e.preventDefault();
    try {
      const res = await updateDoorInfo({
        doorID: doorInfo.id,
        updatedData: {
          floorID: doorInfo.floorID,
          status: doorStatus === "No status" ? "" : doorStatus,
          sensorId: doorInfo.sensorID,
          alias: doorInfo.alias,
        },
        action: "status",
      });
      //@ts-ignore
      if (res.data) {
        dispatch(getDoors({ floorID: doorInfo.floorID }));
        notifications.show({
          title: "Success",
          message: "Status edited successfully",
          color: "green",
        });
      } else {
        notifications.show({
          title: "Error",
          message: "something went wrong , try again!",
          color: "red",
        });
      }
    } catch (error) {
      console.error("Mutation error:", error);
      notifications.show({
        title: "Error",
        message: "something went wrong , try again!",
        color: "red",
      });
    }
  };

  return (
    <div className="w-full  p-5 sticky top-10 right-0 ">
      <div className="flex items-center justify-between text-grayText text-2xl  mb-10 lg:my-10">
        <h3 className="font-bold text-grayText">Editing Door View</h3>
        <IoMdClose
          className="font-bold hover:cursor-pointer "
          style={{ color: "#404F65 !important" }}
          onClick={() => setOpenControlBar(false)}
        />
      </div>

      <div className="lg:justify-between gap-4 lg:gap-0  items-center flex mb-8 ">
        <h4 className="text-sm  ">Door ID</h4>
        <p className="bg-breadCrumbGray text-white p-1 rounded-md text-sm">
          {doorInfo?.id || `${doorInfo?.alias} (no sensor assigned)`}
        </p>
      </div>

      {doorInfo?.current && (
        <div className="flex items-center justify-between gap-2 mb-8 text-grayText3">
          {[
            {
              label: "Occupancy",
              value: doorInfo.current.currentOccupancy,
              Icon: TbUsers,
            },
            {
              label: "Total In",
              value: doorInfo.current.totalIn,
              Icon: TbDoorEnter,
            },
            {
              label: "Total Out",
              value: doorInfo.current.totalOut,
              Icon: TbDoorExit,
            },
          ].map(({ label, value, Icon }) => (
            <div key={label} className="flex items-center gap-1.5" title={label}>
              <Icon className="shrink-0" size={16} />
              <span className="font-bold text-grayText">{value ?? "—"}</span>
              <span className="text-sm">{label}</span>
            </div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1  min-[400px]:grid-cols-2 sm:grid-cols-3 lg:grid-cols-1 gap-6  w-full ">
        <form onSubmit={handleEditName} className="flex gap-2 flex-col w-full">
          <div className="flex flex-col">
            <label className="text-sm font-bold pb-[10px] pt-1">
              Door Name
            </label>
            <input
              type="text"
              className="border-lightGray2 rounded-xl  py-1 px-2 border-solid border "
              value={doorName}
              onChange={(e) => setDoorName(e.target.value)}
              disabled={isUnassigned || nameLocked}
            />
            {nameLocked && (
              <span className="text-xs text-grayText3 pt-1">
                The name links this door to its floor-plan shape and can't be
                changed.
              </span>
            )}
          </div>
          <Button
            type="submit"
            color={"#FB6514"}
            disabled={isUnassigned || nameLocked || doorInfo.alias === doorName}
            loading={isLoading && doorInfo.alias !== doorName}
          >
            Save
          </Button>
        </form>

        <form
          onSubmit={handleAssignSensor}
          className="flex gap-2 flex-col  w-full"
        >
          <div className="flex flex-col">
            <h4 className="pb-3 text-sm ">Connected Sensor</h4>
            <SearchableSelect
              data={sensors}
              value={selectedSensor}
              setValue={setSelectedSensor}
            />
          </div>

          <Button
            type="submit"
            disabled={doorInfo.sensorID === selectedSensor}
            color={"#FB6514"}
            loading={isAssigning || isUnassigning}
          >
            Save
          </Button>
        </form>

        <form
          onSubmit={handleEditStatus}
          className="flex gap-2 flex-col  w-full"
        >
          <Select
            label={<h4 className="pb-2">Door status</h4>}
            data={Status}
            placeholder="No status"
            value={Status.some((s) => s.value === doorStatus) ? doorStatus : null}
            onChange={(value) => setDoorStatus(value)}
            disabled={isUnassigned}
          />
          <Button
            type="submit"
            color={"#FB6514"}
            loading={isLoading && doorInfo.status !== doorStatus}
            disabled={isUnassigned || doorInfo.status === doorStatus}
          >
            Save
          </Button>
        </form>

        <Button
          variant="outline"
          className="!w-full mt-10"
          size="md"
          radius={"md"}
          disabled={!doorInfo.sensorID || doorInfo.sensorID === "No sensor"}
          onClick={() => {
            setOpenSensorLogs(true);
          }}
        >
          Sensor Logs
        </Button>
        {openSensorLogs && (
          <SensorLogs
            open={openSensorLogs}
            handleClose={() => {
              setOpenSensorLogs(false);
            }}
            sensorID={doorInfo.sensorID}
            entityLabel="door"
          />
        )}
      </div>
    </div>
  );
};

export default DoorControlBar;
